import { computed, onMounted, ref } from 'vue'
import IdentityManager from '@arcgis/core/identity/IdentityManager'
import OAuthInfo from '@arcgis/core/identity/OAuthInfo'
import Portal from '@arcgis/core/portal/Portal'
import esriRequest from '@arcgis/core/request'
import { ARCGIS_PORTAL_URL } from '@/components/project-detail/constants/constants'
import { useAuthorizationStore } from '@/stores/authorization'

export function useArcgisLogin() {
  const ESRI_JS_API_OAUTH_KEY = 'esriJSAPIOAuth'
  const SESSION_TOKEN_KEY = 'sessionToken'
  const arcgisProxyPortalAppId = import.meta.env.VITE_ARCGIS_PROXY_PORTAL_APP_ID
  const showOrgArcgisAlert = ref(false)
  const authorizationStore = useAuthorizationStore()
  let oAuthInfo: OAuthInfo
  const esriOAuthSessionValue = computed<string>({
    get: () => sessionStorage.getItem(ESRI_JS_API_OAUTH_KEY) || '',
    set: (value) => {
      if (!value) {
        sessionStorage.removeItem(ESRI_JS_API_OAUTH_KEY)
        return
      }
      sessionStorage.setItem(ESRI_JS_API_OAUTH_KEY, value)
    }
  })

  function login() {
    if (IdentityManager && oAuthInfo) {
      IdentityManager.getCredential(oAuthInfo.portalUrl + 'sharing')
    }
  }

  async function reAuthenticateArcgisOnline() {
    try {
      const c = await IdentityManager.checkSignInStatus(ARCGIS_PORTAL_URL + '/sharing')
      const portal = new Portal({
        url: ARCGIS_PORTAL_URL,
        authMode: 'immediate'
      })
      await portal.load()
      const token = c.token
      const portalInfo = await getPortalInfo(token, portal.url)
      const isOrganization = isOrganizationPortal(portalInfo)
      if (!isOrganization) {
        IdentityManager.destroyCredentials()
        authorizationStore.clearToken()
        showOrgArcgisAlert.value = true
      } else {
        authorizationStore.setToken(token)
        showOrgArcgisAlert.value = false
      }
      return true
    } catch (err) {
      login()
      return false
    }
  }

  function getPortalInfo(token: string, portal_url: string) {
    const portalUrl = `${portal_url}/sharing/rest/portals/self`
    return (async () => {
      try {
        const response = await esriRequest(portalUrl, {
          query: {
            f: 'json',
            token: token
          },
          responseType: 'json'
        })
        return response.data
      } catch (error) {
        console.error('Error fetching portal info:', error)
        throw error
      }
    })()
  }

  function isOrganizationPortal(portalInfo: any): boolean {
    let isOrganizationUt = false
    let isOrganizationSub = false
    const userType = portalInfo?.user?.userLicenseTypeId
    if (userType !== undefined && userType !== 'locationPlatformUT') {
      isOrganizationUt = true
    }
    const subscriptionType = portalInfo?.subscriptionInfo?.type
    if (subscriptionType !== undefined && subscriptionType !== 'Location Platform') {
      isOrganizationSub = true
    }
    if (isOrganizationUt !== isOrganizationSub) {
      return false
    }
    return isOrganizationSub
  }
  async function checkArcgisStatus() {
    oAuthInfo = new OAuthInfo({
      appId: arcgisProxyPortalAppId,
      portalUrl: ARCGIS_PORTAL_URL,
      popup: false
    })
    IdentityManager.registerOAuthInfos([oAuthInfo])
    try {
      const c = await IdentityManager.checkSignInStatus(ARCGIS_PORTAL_URL + '/sharing')
      const portal = new Portal({
        url: ARCGIS_PORTAL_URL,
        authMode: 'immediate'
      })
      await portal.load()
      const token = c.token
      const portalInfo = await getPortalInfo(token, portal.url)
      const isOrganization = isOrganizationPortal(portalInfo)
      if (!isOrganization) {
        IdentityManager.destroyCredentials()
        authorizationStore.clearToken()
        showOrgArcgisAlert.value = true
      } else {
        authorizationStore.setToken(token)
        showOrgArcgisAlert.value = false
      }
    } catch (err) {
      authorizationStore.clearToken()
      showOrgArcgisAlert.value = true
    }
  }

  onMounted(async () => {
    oAuthInfo = new OAuthInfo({
      appId: arcgisProxyPortalAppId,
      portalUrl: ARCGIS_PORTAL_URL,
      popup: false
    })

    IdentityManager.registerOAuthInfos([oAuthInfo])

    const savedToken = localStorage.getItem(SESSION_TOKEN_KEY)
    if (savedToken) {
      esriOAuthSessionValue.value = savedToken
    }

    try {
      const c = await IdentityManager.checkSignInStatus(ARCGIS_PORTAL_URL + '/sharing')
      const portal = new Portal({
        url: ARCGIS_PORTAL_URL,
        authMode: 'immediate'
      })

      await portal.load()
      const token = c.token
      try {
        const portalInfo = await getPortalInfo(token, portal.url)
        const isOrganization = isOrganizationPortal(portalInfo)
        if (!isOrganization) {
          IdentityManager.destroyCredentials()
          authorizationStore.clearToken()
          showOrgArcgisAlert.value = true
        } else {
          authorizationStore.setToken(token)
          localStorage.setItem(SESSION_TOKEN_KEY, esriOAuthSessionValue.value)
        }
      } catch (error) {
        IdentityManager.destroyCredentials()
        authorizationStore.clearToken()
        showOrgArcgisAlert.value = true
      }
    } catch (error) {
      authorizationStore.clearToken()
    }
  })

  return {
    showOrgArcgisAlert,
    login,
    checkArcgisStatus,
    reAuthenticateArcgisOnline,
    arcgisProxyPortalAppId
  }
}
